// Public super-admin auth hook (Wave I).
//
// Mirrors `useAuth` for the platform super-admin flow. Tokens live in the
// `superAccessToken` / `superRefreshToken` storage slots, separate from
// the tenant slots so the two realms can coexist in the same browser
// without evicting each other. The axios client routes a request to the
// super token (and the super refresh endpoint) based on the URL — see
// `src/api/client.ts`.

import { useCallback } from 'react';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';
import { storage } from '../utils/storage';
import { useSuperAdminStore } from '../store/superAdminStore';
import type { SuperLoginResult } from '../types/api';

export interface SuperLoginInput {
  username: string;
  password: string;
}

export function useSuperAuth() {
  const status = useSuperAdminStore((s) => s.status);
  const admin = useSuperAdminStore((s) => s.admin);
  const isAuthenticated = useSuperAdminStore((s) => s.isAuthenticated);
  const setAuthenticated = useSuperAdminStore((s) => s.setAuthenticated);
  const setAnonymous = useSuperAdminStore((s) => s.setAnonymous);
  const setLoading = useSuperAdminStore((s) => s.setLoading);

  const login = useCallback(
    async (input: SuperLoginInput) => {
      setLoading();
      const res = await apiClient.post<SuperLoginResult, SuperLoginInput>(
        endpoints.superAuth.login,
        input,
      );
      const { access_token, refresh_token, admin: loginAdmin } = res.data;
      storage.set('superAccessToken', access_token);
      storage.set('superRefreshToken', refresh_token);
      setAuthenticated(loginAdmin);
      return loginAdmin;
    },
    [setAuthenticated, setLoading],
  );

  const logout = useCallback(async () => {
    try {
      await apiClient.raw.post(endpoints.superAuth.logout);
    } catch {
      // Even if the server call fails, treat the user as logged out client-side.
    }
    // Only clear the super-admin slots — a concurrent tenant session in
    // the same browser must survive a super-admin logout.
    storage.clearSuper();
    setAnonymous();
  }, [setAnonymous]);

  return {
    status,
    admin,
    isAuthenticated,
    login,
    logout,
  };
}
